/// <amd-module name="Release/WorkItemReleaseLinks/ReleaseDomainService" />
import {
    ApprovalStatus,
    DeployPhaseStatus,
    DeploymentAttempt,
    DeploymentJob,
    DeploymentOperationStatus,
    DeploymentStatus,
    EnvironmentStatus,
    GateStatus,
    ManualIntervention,
    ManualInterventionStatus,
    ReleaseApproval,
    ReleaseDeployPhase,
    ReleaseEnvironment,
    ReleaseGates,
    ReleaseTask,
    TaskStatus,
} from "Release/Client/Contracts/Release";
import { localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import {
    IVssService,
    VssService,
    IVssPageContext,
    Services,
} from "VSS/Platform/Context";
import { IVssPageService } from "VSS/Platform/Page";

export enum ComputedReleaseEnvironmentStatus {
    Undefined = 0,
    NotStarted = 1,
    Pending = 2,
    Succeeded = 3,
    Rejected = 4,
    InProgress = 5,
    Canceled = 6,
    Queued = 7,
    Deferred = 8,
    QueuedForAgent = 9,
    PreApprovalPending = 10,
    Failed = 11,
    PreApprovalRejected = 12,
    Scheduled = 13,
    PartiallySucceeded = 14,
    QueuedForPipeline = 15,
    Cancelling = 16,
    GatesInProgress = 17,
    GatesFailed = 18,
    GatesStabilizing = 19,
}

enum GatesInProgressSubStatus {
    NotInProgress = 1,
    Stabilizing = 2,
    Evaluating = 3,
}

export enum ApprovalType {
    PreApproval = 1,
    PostApproval = 2,
    PreGates = 3,
    PostGates = 4,
}

export interface IReleaseDomainService extends IVssService {
    getComputedEnvironmentStatusFromDeployment(
        deployment: DeploymentAttempt | undefined
    ): ComputedReleaseEnvironmentStatus;

    getLatestDeployment(deploySteps: DeploymentAttempt[]): DeploymentAttempt;

    getMaxAttemptNumberFromDeploySteps(deploySteps: DeploymentAttempt[]): number;

    environmentHasPendingApproval(
        environment: ReleaseEnvironment,
        approvalType: ApprovalType,
        attempt?: number
    ): boolean;

    hasManualIntervention(environment: ReleaseEnvironment): boolean;
}

class ReleaseDomainService extends VssService implements IReleaseDomainService {
    private _deploymentStatusMap: Map<
        DeploymentStatus,
        ComputedReleaseEnvironmentStatus
    > = new Map();

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        this._deploymentStatusMap = new Map();
        this._deploymentStatusMap.set(
            DeploymentStatus.Undefined,
            ComputedReleaseEnvironmentStatus.Undefined
        );
        this._deploymentStatusMap.set(
            DeploymentStatus.NotDeployed,
            ComputedReleaseEnvironmentStatus.NotStarted
        );
        this._deploymentStatusMap.set(
            DeploymentStatus.InProgress,
            ComputedReleaseEnvironmentStatus.InProgress
        );
        this._deploymentStatusMap.set(
            DeploymentStatus.Succeeded,
            ComputedReleaseEnvironmentStatus.Succeeded
        );
        this._deploymentStatusMap.set(
            DeploymentStatus.PartiallySucceeded,
            ComputedReleaseEnvironmentStatus.PartiallySucceeded
        );
        this._deploymentStatusMap.set(
            DeploymentStatus.Failed,
            ComputedReleaseEnvironmentStatus.Rejected
        );
    }

    public getComputedEnvironmentStatusFromDeployment(
        deployment: DeploymentAttempt
    ): ComputedReleaseEnvironmentStatus {
        if (!deployment) {
            return ComputedReleaseEnvironmentStatus.NotStarted;
        }

        const status: ComputedReleaseEnvironmentStatus | undefined =
            this._deploymentStatusMap.get(deployment.status);
        if (!status) {
            return ComputedReleaseEnvironmentStatus.NotStarted;
        }

        if (status === ComputedReleaseEnvironmentStatus.NotStarted) {
            switch (deployment.operationStatus) {
                case DeploymentOperationStatus.Queued:
                    return ComputedReleaseEnvironmentStatus.Queued;
                case DeploymentOperationStatus.Pending:
                    return ComputedReleaseEnvironmentStatus.PreApprovalPending;
                case DeploymentOperationStatus.Canceled:
                case DeploymentOperationStatus.PhaseCanceled:
                    return ComputedReleaseEnvironmentStatus.Canceled;
                case DeploymentOperationStatus.Deferred:
                    return ComputedReleaseEnvironmentStatus.Deferred;
                case DeploymentOperationStatus.QueuedForAgent:
                    return ComputedReleaseEnvironmentStatus.QueuedForAgent;
                case DeploymentOperationStatus.QueuedForPipeline:
                    return ComputedReleaseEnvironmentStatus.QueuedForPipeline;
                case DeploymentOperationStatus.Rejected:
                    return ComputedReleaseEnvironmentStatus.PreApprovalRejected;
                case DeploymentOperationStatus.Approved:
                    return ComputedReleaseEnvironmentStatus.InProgress;
                case DeploymentOperationStatus.Cancelling:
                    return ComputedReleaseEnvironmentStatus.Cancelling;
                case DeploymentOperationStatus.EvaluatingGates:
                    return this.getComputedReleaseEnvironmentStatusFromReleaseGates(
                        deployment
                    );
                case DeploymentOperationStatus.GateFailed:
                    return ComputedReleaseEnvironmentStatus.GatesFailed;
            }
        } else if (status === ComputedReleaseEnvironmentStatus.InProgress) {
            switch (deployment.operationStatus) {
                case DeploymentOperationStatus.Pending:
                case DeploymentOperationStatus.ManualInterventionPending:
                    return ComputedReleaseEnvironmentStatus.Pending;
                case DeploymentOperationStatus.Cancelling:
                    return ComputedReleaseEnvironmentStatus.Cancelling;
                case DeploymentOperationStatus.EvaluatingGates:
                    return this.getComputedReleaseEnvironmentStatusFromReleaseGates(
                        deployment
                    );
            }
        } else if (status === ComputedReleaseEnvironmentStatus.Rejected) {
            switch (deployment.operationStatus) {
                case DeploymentOperationStatus.Canceled:
                    return ComputedReleaseEnvironmentStatus.Canceled;
                case DeploymentOperationStatus.Rejected:
                    return ComputedReleaseEnvironmentStatus.Rejected;
                case DeploymentOperationStatus.GateFailed:
                    return ComputedReleaseEnvironmentStatus.GatesFailed;
                default:
                    return ComputedReleaseEnvironmentStatus.Failed;
            }
        }

        return status;
    }

    public getLatestDeployment(
        deploySteps: DeploymentAttempt[]
    ): DeploymentAttempt {
        return deploySteps.reduce((maxObject, currentObject) => {
            return currentObject.attempt > maxObject.attempt
                ? currentObject
                : maxObject;
        }, deploySteps[0]);
    }

    public getMaxAttemptNumberFromDeploySteps(
        deploySteps: DeploymentAttempt[]
    ): number {
        const latestDeployment = this.getLatestDeployment(deploySteps);
        if (!!latestDeployment) {
            return latestDeployment.attempt;
        }

        return 1;
    }

    private _checkCriteriaOnApproval(
        approval: ReleaseApproval,
        checkForPendingOnly: boolean,
        byAnyUser: boolean,
        byAnyGroup: boolean,
        byCurrentUser: boolean
    ) {
        if (approval.status === ApprovalStatus.Pending) {
            if (checkForPendingOnly) {
                return true;
            } else {
                if (!approval.isAutomated) {
                    if (byAnyUser) {
                        return true;
                    } else if (byAnyGroup) {
                        return this.isAssignedToGroup(approval);
                    } else {
                        return byCurrentUser && this.isApproverCurrentUser(approval);
                    }
                }
            }
        }

        return false;
    }

    public environmentHasPendingApproval(
        environment: ReleaseEnvironment,
        approvalType: ApprovalType,
        attempt?: number
    ): boolean {
        return !!this._getAnyPendingApproval(
            environment,
            approvalType,
            true,
            false,
            false,
            false,
            attempt ?? 0
        );
    }

    private _getAnyPendingApproval(
        environment: ReleaseEnvironment,
        approvalType: ApprovalType,
        checkForPendingOnly: boolean,
        byAnyUser: boolean,
        byAnyGroup: boolean,
        byCurrentUser: boolean,
        attempt: number
    ): ReleaseApproval | undefined {
        const approvals: ReleaseApproval[] =
            approvalType === ApprovalType.PreApproval
                ? environment.preDeployApprovals
                : environment.postDeployApprovals;
        const hasTasksExecutionCompleted: boolean =
            approvalType === ApprovalType.PostApproval
                ? this._areTasksCompleted(environment, attempt)
                : true;

        if (
            this._isEnvironmentNeedApproval(environment) &&
            hasTasksExecutionCompleted &&
            approvals
        ) {
            const filteredApprovals: ReleaseApproval[] =
                this._getFilteredApprovalsByAttempt(approvals, attempt);
            return filteredApprovals.find((approval) =>
                this._checkCriteriaOnApproval(
                    approval,
                    checkForPendingOnly,
                    byAnyUser,
                    byAnyGroup,
                    byCurrentUser
                )
            );
        }

        return undefined;
    }

    private isAssignedToGroup(approval: ReleaseApproval): boolean {
        return approval.approver.isContainer;
    }

    private isApproverCurrentUser(approval: ReleaseApproval): boolean {
        const pageData = this.pageContext
            .getService<IVssPageService>("IVssPageService")
            .getData()!;
        const currentUserId = pageData.user.id;
        const canApprove =
            !approval.isAutomated &&
            approval.status === ApprovalStatus.Pending &&
            localeIgnoreCaseComparer(currentUserId, approval.approver.id) === 0;

        return canApprove;
    }

    private getComputedReleaseEnvironmentStatusFromReleaseGates(
        deployment: DeploymentAttempt
    ): ComputedReleaseEnvironmentStatus {
        return this.getDeploymentGatesInProgressSubStatus(deployment) ===
            GatesInProgressSubStatus.Stabilizing
            ? ComputedReleaseEnvironmentStatus.GatesStabilizing
            : ComputedReleaseEnvironmentStatus.GatesInProgress;
    }

    private getDeploymentGatesInProgressSubStatus(
        deployment: DeploymentAttempt
    ): GatesInProgressSubStatus {
        let status: GatesInProgressSubStatus =
            GatesInProgressSubStatus.NotInProgress;
        if (deployment) {
            let gates: ReleaseGates =
                deployment.status === DeploymentStatus.NotDeployed
                    ? deployment.preDeploymentGates
                    : deployment.postDeploymentGates;
            status = this.getGatesInProgressSubStatus(gates);
        }

        return status;
    }

    private getGatesInProgressSubStatus(
        gates: ReleaseGates
    ): GatesInProgressSubStatus {
        let status: GatesInProgressSubStatus =
            GatesInProgressSubStatus.NotInProgress;
        if (gates && gates.status === GateStatus.InProgress) {
            status = !!(gates.startedOn && !gates.stabilizationCompletedOn)
                ? GatesInProgressSubStatus.Stabilizing
                : GatesInProgressSubStatus.Evaluating;
        }

        return status;
    }

    private _areTasksCompleted(
        environment: ReleaseEnvironment,
        attempt: number
    ): boolean {
        const filteredTasks: ReleaseTask[] = this.getTasksForAttempt(
            environment,
            attempt
        );
        const nonSuccessfulTask = filteredTasks.find(
            (filteredTask) => !this.isTaskSuccessful(filteredTask.status)
        );

        return !nonSuccessfulTask;
    }

    private getTasksForAttempt(
        environment: ReleaseEnvironment,
        attempt: number
    ): ReleaseTask[] {
        const filteredTasks: ReleaseTask[] = [];

        if (
            !!environment &&
            environment.deploySteps &&
            environment.deploySteps.length > 0
        ) {
            const deployStep: DeploymentAttempt | undefined =
                environment.deploySteps.find(
                    (step) => !attempt || step.attempt === attempt
                );

            if (deployStep)
                filteredTasks.push(...this._getTasksForDeployStep(deployStep));
        }

        return filteredTasks;
    }

    private isTaskSuccessful(state: TaskStatus): boolean {
        switch (state) {
            case TaskStatus.Succeeded:
            case TaskStatus.Skipped:
            case TaskStatus.PartiallySucceeded:
                return true;
        }
        return false;
    }

    private _getTasksForDeployStep(deployStep: DeploymentAttempt) {
        const allTasks: ReleaseTask[] = [];

        if (!!deployStep && !!deployStep.releaseDeployPhases) {
            deployStep.releaseDeployPhases.forEach((dp: ReleaseDeployPhase) =>
                dp.deploymentJobs.forEach((job: DeploymentJob) =>
                    allTasks.push(...job.tasks)
                )
            );
        }

        return allTasks;
    }

    private _isEnvironmentNeedApproval(environment: ReleaseEnvironment): boolean {
        return environment.status === EnvironmentStatus.InProgress;
    }

    private _getFilteredApprovalsByAttempt(
        approvals: ReleaseApproval[],
        attempt: number
    ): ReleaseApproval[] {
        return attempt
            ? approvals.filter((approval: ReleaseApproval) => {
                return approval.attempt === attempt;
            })
            : approvals;
    }

    public hasManualIntervention(environment: ReleaseEnvironment): boolean {
        const manualInterventions: ManualIntervention[] =
            this.getManualInterventionsForEnvironment(environment);
        if (!!manualInterventions && manualInterventions.length > 0) {
            return true;
        }

        return false;
    }

    private getManualInterventionsForEnvironment(
        environment: ReleaseEnvironment
    ): ManualIntervention[] {
        const manualInterventions: ManualIntervention[] = [];
        if (environment.status === EnvironmentStatus.InProgress) {
            const latestAttempt = this.getLatestDeploymentAttempt(
                environment.deploySteps
            );
            if (!!latestAttempt) {
                const currentReleaseDeployPhase =
                    latestAttempt.releaseDeployPhases.find(
                        (releaseDeployPhase) =>
                            releaseDeployPhase.status === DeployPhaseStatus.InProgress
                    );
                if (!!currentReleaseDeployPhase) {
                    const manualIntervention =
                        currentReleaseDeployPhase.manualInterventions.find(
                            (manualIntervention) =>
                                manualIntervention.status === ManualInterventionStatus.Pending
                        );
                    if (!!manualIntervention) {
                        manualInterventions.push(manualIntervention);
                    }
                }
            }
        }

        return manualInterventions;
    }

    private getLatestDeploymentAttempt(
        deploySteps: DeploymentAttempt[]
    ): DeploymentAttempt | undefined {
        if (deploySteps && deploySteps.length > 0) {
            let maxAttemptNumber: number = 0;
            let latestDeployment: DeploymentAttempt | undefined = undefined;

            // No guarantee that list is in sorted order
            deploySteps.forEach((step: DeploymentAttempt) => {
                if (step.attempt && maxAttemptNumber < step.attempt) {
                    maxAttemptNumber = step.attempt;
                    latestDeployment = step;
                }
            });

            return latestDeployment;
        }

        return undefined;
    }
}

Services.add("IReleaseDomainService", { serviceFactory: ReleaseDomainService });
